import { useState, type ReactNode } from 'react';
import { Message, Sparks } from 'iconoir-react';
import { Loader2, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ResourceCard, ResourceCardLogo, ResourceCardMenu } from 'components/blocks/resource-card';
import { useTheme } from 'components/core/theme-provider';
import { Badge } from 'components/ui/badge';
import { Card, CardContent } from 'components/ui/card';
import type { ITool, ToolStatus, ToolType } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import {
  useGetComposioToolkitCatalog,
  useGetComposioToolkits,
  useGetOpenConnectorCatalog,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { cn } from 'lib/utils';
import { useIsOwner } from 'hooks/use-is-owner';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import { resolveToolProviderIcon } from 'pages/agents/utils/agent-tool-provider-icons';
import { connectorCatalogLogo, openCatalogIcon, openConnectorSlug } from './components/catalog-tool-logo';
import { resolveComposioToolLogo } from './components/composio-logos';
import { getComposioConfig, getToolVisibility } from './components/tool-detail-utils';
import { EditComposioToolModal } from './components/edit-composio-tool-modal';
import { EditOpenConnectorToolModal } from './components/edit-open-connector-tool-modal';
import { EditMcpServerModal } from './components/edit-mcp-server-modal';
import { EditToolModal } from './components/edit-tool-modal';
import { canToggleToolStatus, ToolStatusSwitch } from './components/tool-status-toggle';
import { AddFolderToAgentDialog } from 'pages/knowledge-base/components/add-folder-to-agent-dialog';
import { configureCardGridClassName, configureCardSizeClassName } from 'constant/configure-layout';

export type ViewMode = 'grid' | 'list';

export const toolsGridClassName = configureCardGridClassName;

export const toolsSectionHeadingClassName = 'text-sm font-medium text-muted-foreground';

export function ToolsSectionHeadingSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('h-5 w-28 animate-pulse rounded-md bg-muted', className)}
      aria-hidden
    />
  );
}

export const toolsListPaginationClassName =
  'mt-6 flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between';

export const toolsListPaginationControlsClassName = 'flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center';

export const getToolTypeLabel = (type: ToolType) => {
  if (type === 'mcp_server') return 'MCP Server';
  if (type === 'composio') return 'Composio';
  if (type === 'open_connector') return 'Open Connector';
  if (type === 'webhook') return 'Webhook';
  if (type === 'graphql') return 'GraphQL';
  return 'Tool';
};

export const getToolTypeShortLabel = (
  type: ToolType,
  translate: (key: string) => string
) => {
  if (type === 'mcp_server') return translate('MCP') || 'MCP';
  if (type === 'composio') return translate('CONNECTOR') || 'Connector';
  if (type === 'open_connector') return translate('OPEN_CONNECTORS') || 'Open Connectors';
  if (type === 'webhook') return translate('WEBHOOK') || 'Webhook';
  if (type === 'graphql') return translate('GRAPHQL') || 'GraphQL';
  return translate('TOOL') || 'Tool';
};

export const getStatusVariant = (status: ToolStatus) => {
  if (status === 'active') return 'default';
  if (status === 'maintenance') return 'secondary';
  return 'outline';
};

const toolTypeBadgeClassName =
  'w-fit rounded-md border-0 bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground';

function ToolTypeAndVisibilityBadges({ tool }: Readonly<{ tool: ITool }>) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className={toolTypeBadgeClassName}>
        {getToolTypeShortLabel(tool.type, t)}
      </Badge>
      <VisibilityBadge value={getToolVisibility(tool)} className="shrink-0" />
    </div>
  );
}

function ConnectorCardLogo({ tool }: Readonly<{ tool: ITool }>) {
  const isOpenConnector = tool.type === 'open_connector';
  const isComposio = tool.type === 'composio';
  const fromConfig = isOpenConnector
    ? openConnectorSlug(tool)
    : getComposioConfig(tool).toolkits[0] ?? '';
  const toolkitsQuery = useGetComposioToolkits(tool.id, {
    enabled: isComposio && !fromConfig,
  });
  const catalogQuery = useGetComposioToolkitCatalog({
    enabled: isComposio || isOpenConnector,
  });
  // One shared catalog for every card. Notion's icon lives here, not in the toolkit catalog.
  const openCatalogQuery = useGetOpenConnectorCatalog(undefined, {
    enabled: isOpenConnector,
    limit: 200,
  });
  const toolkit = fromConfig || toolkitsQuery.data?.toolkits?.[0]?.toolkit || '';
  const toolkits = catalogQuery.data?.toolkits ?? [];
  let logo: string | null = null;
  if (isOpenConnector) {
    logo =
      connectorCatalogLogo(tool, toolkits, toolkit) ||
      openCatalogIcon(openCatalogQuery.data?.services, toolkit || tool.name);
    // Wait for the open catalog so a generic provider icon does not flash before the real one.
    if (!logo && !openCatalogQuery.isPending && !openCatalogQuery.isFetching) {
      logo = resolveToolProviderIcon(toolkit || tool.name);
    }
  } else if (isComposio) {
    const slug = toolkit.trim().toUpperCase();
    const catalogLogo = slug ? toolkits.find((entry) => entry.slug.trim().toUpperCase() === slug)?.logo : null;
    logo = resolveComposioToolLogo(toolkit) || catalogLogo || null;
  }
  return <ResourceCardLogo src={logo} name={tool.name} />;
}

const isConnectorToolType = (type: ITool['type']) =>
  type === 'composio' || type === 'open_connector';

function ConnectorStatusBadge({ tool }: Readonly<{ tool: ITool }>) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isActive = tool.status === 'active';
  const label = isActive
    ? t('ACTIVE', { defaultValue: 'Active' })
    : tool.status === 'inactive'
      ? t('INACTIVE', { defaultValue: 'Inactive' })
      : tool.status === 'maintenance'
        ? t('MAINTENANCE', { defaultValue: 'Maintenance' })
        : tool.status === 'deprecated'
          ? t('DEPRECATED', { defaultValue: 'Deprecated' })
          : tool.status;

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded px-2 py-1 text-xs font-semibold leading-heading',
        isActive
          ? 'bg-success-50 text-success-600'
          : cn(
              'bg-grey-50 dark:bg-white/10',
              theme === 'dark' ? 'text-muted-foreground' : 'text-grey-500'
            )
      )}
    >
      <Message width={14} height={14} strokeWidth={1.75} />
      {label}
    </span>
  );
}

const getToolDescription = (tool: ITool, translate: (key: string, options?: { defaultValue?: string }) => string) =>
  tool.description || translate('NO_DESCRIPTION', { defaultValue: 'No description available.' });

/** Grid tile shared by `ToolCard` and `BlocksToolCard`. */
function ToolGridCard({
  tool,
  headerActions,
  statusSwitch,
  onSelect,
  className,
}: Readonly<{
  tool: ITool;
  headerActions: ReactNode;
  statusSwitch?: ReactNode;
  onSelect: () => void;
  className?: string;
}>) {
  const { t } = useTranslation();
  const isConnector = isConnectorToolType(tool.type);

  return (
    <ResourceCard
      title={tool.name}
      description={getToolDescription(tool, t)}
      icon={
        isConnector ? (
          <ConnectorCardLogo tool={tool} />
        ) : (
          <ResourceCardLogo name={tool.name}>
            <Wrench className="h-5 w-5" />
          </ResourceCardLogo>
        )
      }
      actions={headerActions}
      footer={
        <div className="flex min-h-7 items-center justify-between gap-2">
          {statusSwitch}
          <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
            <VisibilityBadge
              value={getToolVisibility(tool)}
              uppercase={false}
              className="shrink-0"
            />
          </div>
        </div>
      }
      onSelect={onSelect}
      className={cn(configureCardSizeClassName, className)}
    />
  );
}

/** List row shared by `ToolCard` and `BlocksToolCard`. */
function ToolListCard({
  tool,
  headerActions,
  onSelect,
  className,
}: Readonly<{
  tool: ITool;
  headerActions: ReactNode;
  onSelect: () => void;
  className?: string;
}>) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isConnector = isConnectorToolType(tool.type);
  const description = getToolDescription(tool, t);
  const titleColor = theme === 'dark' ? 'text-foreground' : 'text-grey-700';
  const descriptionColor = theme === 'dark' ? 'text-muted-foreground' : 'text-grey-700';

  return (
    <Card
      className={cn(
        'group flex h-auto min-w-0 cursor-pointer items-center gap-4 rounded-lg bg-card p-4 text-card-foreground transition-colors',
        isConnector
          ? 'border border-grey-50 shadow-none dark:border-border dark:bg-card dark:hover:bg-muted/40'
          : 'border border-border shadow-sm transition-[border-color,background-color,opacity] hover:border-border hover:bg-accent/5',
        className
      )}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      {isConnector ? (
        <div className="flex shrink-0 items-center gap-2">
          <ConnectorCardLogo tool={tool} />
          <VisibilityBadge value={getToolVisibility(tool)} className="shrink-0" />
        </div>
      ) : (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Wrench className="h-5 w-5" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <h3
            className={cn('min-w-0 flex-1 truncate text-base font-semibold', titleColor)}
            title={tool.name}
          >
            {tool.name}
          </h3>
          {headerActions}
        </div>
        {isConnector ? null : (
          <div className="mt-2">
            <ToolTypeAndVisibilityBadges tool={tool} />
          </div>
        )}
        <p className={cn('mt-2 line-clamp-2 text-sm', descriptionColor)} title={description}>
          {description}
        </p>
        {isConnector ? (
          <div className="mt-3">
            <ConnectorStatusBadge tool={tool} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function ToolCardHeaderActions({
  tool,
  projectKey,
  isAdmin,
  includeSwitch = true,
  children,
}: Readonly<{
  tool: ITool;
  projectKey: string;
  isAdmin: boolean;
  includeSwitch?: boolean;
  children?: ReactNode;
}>) {
  const showSwitch = includeSwitch && canToggleToolStatus(tool);
  const hasMenu = Boolean(children);

  if (!showSwitch && !hasMenu) {
    return null;
  }

  return (
    <div
      className="flex shrink-0 items-center gap-0.5"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      {showSwitch ? <ToolStatusSwitch tool={tool} projectKey={projectKey} isAdmin={isAdmin} /> : null}
      {children}
    </div>
  );
}

function ToolCardActionsMenu({
  tool,
  allowEdit,
  allowDelete,
  isDeleting,
  onDelete,
  onEdit,
  projectKey,
}: {
  tool: ITool;
  allowEdit?: boolean;
  allowDelete?: boolean;
  isDeleting?: boolean;
  onDelete?: (tool: ITool) => void;
  onEdit: () => void;
  projectKey: string;
}) {
  const { t } = useTranslation();
  const [isAddToAgentOpen, setIsAddToAgentOpen] = useState(false);

  return (
    <>
      <ResourceCardMenu
        ariaLabel={t('ACTIONS_FOR', { name: tool.name, defaultValue: 'Actions for {{name}}' })}
        items={[
          allowEdit && { label: t('EDIT') || 'Edit', icon: <Pencil />, onClick: onEdit },
          {
            label: t('ADD_TO_AGENT') || 'Add to agent',
            icon: <Sparks strokeWidth={1.5} />,
            onClick: () => setIsAddToAgentOpen(true),
          },
          allowDelete && {
            label: t('DELETE') || 'Delete',
            icon: isDeleting ? <Loader2 className="animate-spin" /> : <Trash2 />,
            onClick: () => onDelete?.(tool),
            destructive: true,
            disabled: isDeleting,
          },
        ]}
      />

      <AddFolderToAgentDialog
        open={isAddToAgentOpen}
        onOpenChange={setIsAddToAgentOpen}
        projectKey={projectKey}
        resourceId={tool.id}
        queryParam="addTool"
        description={
          t('SELECT_AGENT_TO_CONNECT_TOOL') || 'Select an agent to connect this tool.'
        }
      />
    </>
  );
}

function BlocksToolActionsMenu({
  tool,
  allowEdit,
  allowDelete,
  isAdding,
  isDeleting,
  onAdd,
  onEdit,
  onDelete,
}: {
  tool: ITool;
  allowEdit?: boolean;
  allowDelete?: boolean;
  isAdding?: boolean;
  isDeleting?: boolean;
  onAdd: () => void;
  onEdit: () => void;
  onDelete?: (tool: ITool) => void;
}) {
  const { t } = useTranslation();

  return (
    <ResourceCardMenu
      ariaLabel={t('ACTIONS_FOR', { name: tool.name, defaultValue: 'Actions for {{name}}' })}
      items={[
        {
          label: isAdding ? t('ADDING') || 'Adding...' : t('ADD') || 'Add',
          icon: isAdding ? <Loader2 className="animate-spin" /> : <Plus />,
          onClick: onAdd,
          disabled: isAdding,
        },
        allowEdit && { label: t('EDIT') || 'Edit', icon: <Pencil />, onClick: onEdit },
        allowDelete && {
          label: t('DELETE') || 'Delete',
          icon: isDeleting ? <Loader2 className="animate-spin" /> : <Trash2 />,
          onClick: () => onDelete?.(tool),
          destructive: true,
          disabled: isDeleting,
        },
      ]}
    />
  );
}

export function BlocksToolsListSkeleton({ view = 'grid' }: { view?: ViewMode }) {
  const isList = view === 'list';

  return (
    <div className={isList ? 'space-y-3' : toolsGridClassName}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index}>
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-5 w-40 animate-pulse rounded bg-muted" />
              <div className="h-5 w-12 animate-pulse rounded-full bg-muted" />
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function BlocksToolCard({
  tool,
  view = 'grid',
  isSelected,
  allowEdit,
  allowDelete,
  isAdding,
  isDeleting,
  onSelect,
  onAdd,
  onDelete,
  projectKey,
  isAdmin,
}: {
  tool: ITool;
  view?: ViewMode;
  isSelected: boolean;
  allowEdit?: boolean;
  allowDelete?: boolean;
  isAdding?: boolean;
  isDeleting?: boolean;
  projectKey: string;
  isAdmin: boolean;
  onSelect: (tool: ITool) => void;
  onAdd?: (tool: ITool) => void;
  onDelete?: (tool: ITool) => void;
}) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const showMenu = allowEdit || allowDelete;
  const isOff = tool.status === 'inactive';

  const headerMenu = showMenu ? (
    <BlocksToolActionsMenu
      tool={tool}
      allowEdit={allowEdit}
      allowDelete={allowDelete}
      isAdding={isAdding}
      isDeleting={isDeleting}
      onAdd={() => onAdd?.(tool)}
      onEdit={() => setIsEditOpen(true)}
      onDelete={onDelete}
    />
  ) : null;

  const headerActions = (
    <ToolCardHeaderActions
      tool={tool}
      projectKey={projectKey}
      isAdmin={isAdmin}
      includeSwitch={view === 'list'}
    >
      {headerMenu}
    </ToolCardHeaderActions>
  );
  const statusSwitch = (
    <ToolStatusSwitch tool={tool} projectKey={projectKey} isAdmin={isAdmin} />
  );
  const cardClassName = cn(
    isSelected && 'bg-accent/5',
    isOff && canToggleToolStatus(tool) && 'opacity-70'
  );

  return (
    <>
      {view === 'list' ? (
        <ToolListCard
          tool={tool}
          headerActions={headerActions}
          onSelect={() => onSelect(tool)}
          className={cardClassName}
        />
      ) : (
        <ToolGridCard
          tool={tool}
          headerActions={headerActions}
          statusSwitch={statusSwitch}
          onSelect={() => onSelect(tool)}
          className={cardClassName}
        />
      )}

      {allowEdit && tool.type === 'mcp_server' ? (
        <EditMcpServerModal
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description || '',
            status: tool.status,
            visibility: getToolVisibility(tool),
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool
        />
      ) : null}
      {allowEdit && tool.type === 'composio' ? (
        <EditComposioToolModal
          mode="edit"
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description || '',
            status: tool.status,
            visibility: getToolVisibility(tool),
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool
        />
      ) : null}
      {allowEdit && tool.type === 'open_connector' ? (
        <EditOpenConnectorToolModal
          mode="edit"
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description || '',
            status: tool.status,
            visibility: getToolVisibility(tool),
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
        />
      ) : null}
      {allowEdit && tool.type !== 'mcp_server' && tool.type !== 'composio' && tool.type !== 'open_connector' ? (
        <EditToolModal
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            url: '',
            description: tool.description || '',
            status: tool.status,
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool
        />
      ) : null}
    </>
  );
}

export function ToolsListSkeleton({ view }: { view: ViewMode }) {
  const isList = view === 'list';

  return (
    <div className={view === 'grid' ? toolsGridClassName : 'space-y-3'}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index}>
          {isList ? (
            <CardContent className="flex items-center gap-4 p-4">
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-muted" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-32 animate-pulse rounded bg-muted" />
                  <div className="h-5 w-12 animate-pulse rounded-full bg-muted" />
                </div>
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
              </div>
            </CardContent>
          ) : (
            <CardContent className="p-5">
              <div className="animate-pulse space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div className="h-5 w-36 rounded bg-muted" />
                    <div className="h-4 w-20 rounded bg-muted" />
                  </div>
                  <div className="h-8 w-8 rounded bg-muted" />
                </div>
                <div className="h-4 w-full rounded bg-muted" />
                <div className="h-4 w-5/6 rounded bg-muted" />
              </div>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

export function ToolCard({
  tool,
  view,
  allowEdit,
  allowDelete,
  onDelete,
  isDeleting,
  onSelect,
  projectKey,
  isAdmin,
}: {
  tool: ITool;
  view: ViewMode;
  allowEdit?: boolean;
  allowDelete?: boolean;
  onDelete?: (tool: ITool) => void;
  isDeleting?: boolean;
  projectKey: string;
  isAdmin: boolean;
  onSelect?: (tool: ITool) => void;
}) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const showMenu = allowEdit || allowDelete;
  const isOwner = useIsOwner(tool);
  const isOff = tool.status === 'inactive';

  const headerMenu = showMenu ? (
    <ToolCardActionsMenu
      tool={tool}
      allowEdit={allowEdit}
      allowDelete={allowDelete}
      isDeleting={isDeleting}
      onDelete={onDelete}
      onEdit={() => setIsEditOpen(true)}
      projectKey={projectKey}
    />
  ) : null;

  const headerActions = (
    <ToolCardHeaderActions
      tool={tool}
      projectKey={projectKey}
      isAdmin={isAdmin}
      includeSwitch={view === 'list'}
    >
      {headerMenu}
    </ToolCardHeaderActions>
  );
  const statusSwitch = (
    <ToolStatusSwitch tool={tool} projectKey={projectKey} isAdmin={isAdmin} />
  );
  const cardClassName = cn(isOff && canToggleToolStatus(tool) && !isOwner && 'opacity-70');

  return (
    <>
      {view === 'list' ? (
        <ToolListCard
          tool={tool}
          headerActions={headerActions}
          onSelect={() => onSelect?.(tool)}
          className={cardClassName}
        />
      ) : (
        <ToolGridCard
          tool={tool}
          headerActions={headerActions}
          statusSwitch={statusSwitch}
          onSelect={() => onSelect?.(tool)}
          className={cardClassName}
        />
      )}

      {allowEdit && tool.type === 'mcp_server' ? (
        <EditMcpServerModal
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description || '',
            status: tool.status,
            visibility: getToolVisibility(tool),
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool={tool.is_open_tool}
        />
      ) : null}
      {allowEdit && tool.type === 'composio' ? (
        <EditComposioToolModal
          mode="edit"
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description || '',
            status: tool.status,
            visibility: getToolVisibility(tool),
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool={tool.is_open_tool}
        />
      ) : null}
      {allowEdit && tool.type === 'open_connector' ? (
        <EditOpenConnectorToolModal
          mode="edit"
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            description: tool.description || '',
            status: tool.status,
            visibility: getToolVisibility(tool),
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
        />
      ) : null}
      {allowEdit && tool.type !== 'mcp_server' && tool.type !== 'composio' && tool.type !== 'open_connector' ? (
        <EditToolModal
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          tool={{
            id: tool.id,
            name: tool.name,
            url: '',
            description: tool.description || '',
            status: tool.status,
            created_by: tool.created_by,
            CreatedBy: tool.CreatedBy,
          }}
          isBlocksTool={tool.is_open_tool}
        />
      ) : null}
    </>
  );
}
